import { useState } from 'react'
import { ChevronDown, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PackagingItem } from '@/shared/hooks/api/packagingSuppliers'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { LexAssignPackagingItemSheet } from './LexAssignPackagingItemSheet'
import type { AddPackagingItemTarget } from './SupplierDetailPackagingCatalog'

type PendingPackagingAction = 'add' | 'assign'

type LexAddPackagingToPartMenuProps = {
  supplierId: string
  packagedItemId: string
  packedItemSku: string
  hasNoPackaging: boolean
  assignedPackagingItemIds: ReadonlySet<string>
  packagingItems: PackagingItem[]
  hasSupplierAddress: boolean
  onAddItem: (
    target: Extract<AddPackagingItemTarget, { type: 'packedItem' }>
  ) => void
}

export function LexAddPackagingToPartMenu({
  supplierId,
  packagedItemId,
  packedItemSku,
  hasNoPackaging,
  assignedPackagingItemIds,
  packagingItems,
  hasSupplierAddress,
  onAddItem,
}: LexAddPackagingToPartMenuProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [assignSheetOpen, setAssignSheetOpen] = useState(false)
  const [pendingAction, setPendingAction] =
    useState<PendingPackagingAction | null>(null)

  const runAction = (action: PendingPackagingAction) => {
    if (action === 'add') {
      onAddItem({
        type: 'packedItem',
        packagedItemId,
        packedItemSku,
      })
      return
    }
    setAssignSheetOpen(true)
  }

  const requestAction = (action: PendingPackagingAction) => {
    if (hasNoPackaging) {
      setPendingAction(action)
      return
    }
    runAction(action)
  }

  if (!hasSupplierAddress) {
    return (
      <Button type='button' variant='outline' size='xs' disabled>
        <Plus className='size-3' />
        {t('details.partPackaging.addOrAssignPackaging')}
      </Button>
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type='button' variant='outline' size='xs'>
            <Plus className='size-3' />
            {t('details.partPackaging.addOrAssignPackaging')}
            <ChevronDown className='size-3 opacity-70' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem onSelect={() => requestAction('add')}>
            {t('details.packagingCatalog.addForPackedItem')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => requestAction('assign')}>
            {t('details.packagingCatalog.assignPackagingItemOption')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <LexAssignPackagingItemSheet
        open={assignSheetOpen}
        onOpenChange={setAssignSheetOpen}
        supplierId={supplierId}
        packagedItemId={packagedItemId}
        assignedPackagingItemIds={assignedPackagingItemIds}
        packagingItems={packagingItems}
      />
      <AlertDialog
        open={pendingAction != null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingAction(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('packedItems.noPackaging.confirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('packedItems.noPackaging.confirmBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('packedItems.noPackaging.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingAction == null) {
                  return
                }
                const action = pendingAction
                setPendingAction(null)
                runAction(action)
              }}
            >
              {t('packedItems.noPackaging.confirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
